import type {
  BatchCommandsArrayUniversal, BatchCommandsObjectUniversal,
  BatchNamedCommandsUniversal,
  ICallBatchOptions,
  ICallBatchResult, TypeCallParams,
  TypeHttp
} from '../../types/http'
import type { AuthActions } from '../../types/auth'
import type { RestrictionParams } from '../../types/limiters'
import type { BatchResponsePayload } from '../interaction/batch/abstract-interaction-batch'
import type { Result } from '../result'
import { AbstractHttp } from './abstract-http'
import { ApiVersion } from '../../types/b24'
import { AjaxError } from './ajax-error'
import { InteractionBatchV3 } from '../interaction/batch/v3'
import { ProcessingAsArrayV3 } from '../interaction/batch/processing/v3/as-array'
import { ProcessingAsObjectV3 } from '../interaction/batch/processing/v3/as-object'

/**
 * HTTP transport for Bitrix24 REST API v3.
 *
 * Extends {@link AbstractHttp} with v3-specific batch execution: dispatches
 * `batch` calls through {@link InteractionBatchV3} and selects either
 * {@link ProcessingAsArrayV3} or {@link ProcessingAsObjectV3} depending on
 * the shape of the commands argument. Use this transport when the target
 * Bitrix24 portal supports the REST v3 protocol.
 *
 * @link https://bitrix24.github.io/b24jssdk/
 * @link https://apidocs.bitrix24.com/api-reference/rest-v3/index.html
 */
export class HttpV3 extends AbstractHttp implements TypeHttp {
  constructor(
    authActions: AuthActions,
    options?: null | object,
    restrictionParams?: Partial<RestrictionParams>
  ) {
    super(authActions, options, restrictionParams)
    this._version = ApiVersion.v3
  }

  // region batch ////
  public async batch<T = unknown>(
    calls: BatchCommandsArrayUniversal | BatchCommandsObjectUniversal | BatchNamedCommandsUniversal,
    options?: ICallBatchOptions
  ): Promise<Result<ICallBatchResult<T>>> {
    const opts = {
      isHaltOnError: true,
      ...options
    }

    const requestId = opts.requestId ?? this._requestIdGenerator.getRequestId()

    this._logBatchStart(requestId, calls, opts)

    const interactionBatch = new InteractionBatchV3({
      requestId,
      parallelDefaultValue: !(opts.isHaltOnError),
      restrictionManager: this._restrictionManager
    })

    if (Array.isArray(calls)) {
      interactionBatch.setProcessingStrategy(new ProcessingAsArrayV3())
    } else {
      interactionBatch.setProcessingStrategy(new ProcessingAsObjectV3())
    }

    interactionBatch.addCommands(calls)

    if (interactionBatch.size > interactionBatch.maxSize) {
      throw new AjaxError({
        code: 'JSSDK_BATCH_TOO_LARGE',
        description: `Batch too large: ${interactionBatch.size} commands (max: ${interactionBatch.maxSize})`,
        status: 400,
        requestInfo: { method: 'batch', params: { cmd: calls }, requestId },
        originalError: null
      })
    }

    if (interactionBatch.size === 0) {
      throw new AjaxError({
        code: 'JSSDK_BATCH_EMPTY',
        description: 'Batch must contain at least one command',
        status: 400,
        requestInfo: { method: 'batch', params: { cmd: calls }, requestId },
        originalError: null
      })
    }

    const responseBatch = await this.call<BatchResponsePayload<T>>(
      'batch',
      // A cast, and an honest one: `restApi:v3` sends the commands AS the request
      // body — there is no `{ halt, cmd }` envelope to wrap them in — while
      // `call` types its params as `TypeCallParams`. So the request body is not
      // call params on this path either, and the cast is what bridges that until
      // `call` distinguishes the two. See BatchRequestEnvelopeV2 for the v2 side.
      interactionBatch.getCommandsForCall() as TypeCallParams,
      requestId
    )

    const response = await interactionBatch.prepareResponse<T>(responseBatch)

    // Log the results
    this._logBatchCompletion(
      requestId,
      response.getData()?.result?.size ?? 0,
      response.getErrorMessages().length
    )

    return response
  }
  // endregion ////
}
